import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { PRIVACY_POLICY_LINK, TERMS_OF_SERVICE_LINK } from "constant";
import { Col, Container, Row } from "react-bootstrap";
import LegalDocumentsModal from "components/modals/LegalDocumentsModal";
import { APP_CONFIG } from "config/appConfig";

interface LicenseInfo {
    name: string;
    version: string;
    license: string;
    repository?: string;
    publisher?: string;
    url?: string;
}

const ITEMS_PER_PAGE = 20;

const About = () => {
    const [allLicenses, setAllLicenses] = useState<LicenseInfo[]>([]);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const [showLegalDocumentsModal, setShowLegalDocumentsModal] =
        useState(false);
    const [legalDocumentType, setLegalDocumentType] = useState<
        "privacy" | "terms"
    >("terms");

    const handleOpenLegalDocumentsModal = (
        documentType: "privacy" | "terms"
    ) => {
        setLegalDocumentType(documentType);
        setShowLegalDocumentsModal(true);
    };

    const displayedLicenses = useMemo(
        () => allLicenses.slice(0, page * ITEMS_PER_PAGE),
        [allLicenses, page]
    );
    const hasMore = displayedLicenses.length < allLicenses.length;

    const licensesJsonUrl = `${APP_CONFIG.APP_PUBLIC_URL}/licenses.json`;

    useEffect(() => {
        fetch(licensesJsonUrl)
            .then((res) => res.json())
            .then((data: LicenseInfo[]) => {
                setAllLicenses(data);
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
            });
    }, [licensesJsonUrl]);

    const loadMore = useCallback(() => {
        if (isLoadingMore || !hasMore) return;

        setIsLoadingMore(true);
        setTimeout(() => {
            setPage((prev) => prev + 1);
            setIsLoadingMore(false);
        }, 300);
    }, [isLoadingMore, hasMore]);

    useEffect(() => {
        if (isLoading || !loadMoreRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            { threshold: 0.1 }
        );

        observer.observe(loadMoreRef.current);

        return () => observer.disconnect();
    }, [isLoading, loadMore]);

    return (
        <div className="page-content">
            <Container fluid>
                <Row>
                    <h5 className="page-title">About</h5>
                    <Col sm="12" className="mt-2">
                        <div className="empty-widget p-2">
                            <Col sm="12">
                                {APP_CONFIG.APP_IS_ONPREM ? (
                                    <button
                                        type="button"
                                        className="mt-2 mb-2 btn btn-primary"
                                        onClick={() =>
                                            handleOpenLegalDocumentsModal(
                                                "terms"
                                            )
                                        }
                                    >
                                        Terms of Service
                                    </button>
                                ) : (
                                    <a
                                        className="mt-2 mb-2 btn btn-primary"
                                        href={TERMS_OF_SERVICE_LINK}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Terms of Service
                                    </a>
                                )}
                            </Col>
                            <Col sm="12">
                                {APP_CONFIG.APP_IS_ONPREM ? (
                                    <button
                                        type="button"
                                        className="mt-2 mb-2 btn btn-primary"
                                        onClick={() =>
                                            handleOpenLegalDocumentsModal(
                                                "privacy"
                                            )
                                        }
                                    >
                                        Privacy Policy
                                    </button>
                                ) : (
                                    <a
                                        className="mt-2 mb-2 btn btn-primary"
                                        href={PRIVACY_POLICY_LINK}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Privacy Policy
                                    </a>
                                )}
                            </Col>
                            <Col sm="12">
                                <h6>Version: 2.4.0_3.2.1</h6>
                            </Col>
                        </div>

                        <div className="mt-4">
                            <h6 className="mb-3">Open-Source Licenses</h6>
                            <Row className="cstm-table">
                                <Col sm={12}>
                                    <div className="table-head px-3">
                                        <Row>
                                            <Col sm={4}>Library Name</Col>
                                            <Col sm={2}>Version</Col>
                                            <Col sm={3}>License Type</Col>
                                            <Col sm={3}>Copyright/ Author</Col>
                                        </Row>
                                    </div>
                                    {isLoading ? (
                                        <div className="table-row">
                                            <Row>
                                                <Col className="text-center">
                                                    Loading...
                                                </Col>
                                            </Row>
                                        </div>
                                    ) : allLicenses.length === 0 ? (
                                        <div className="table-row">
                                            <Row>
                                                <Col className="text-center">
                                                    No licenses found
                                                </Col>
                                            </Row>
                                        </div>
                                    ) : (
                                        <>
                                            {displayedLicenses.map(
                                                (license) => (
                                                    <div
                                                        className="table-row px-3"
                                                        key={`${license.name}-${license.version}`}
                                                    >
                                                        <Row>
                                                            <Col sm={4}>
                                                                {license.name}
                                                            </Col>
                                                            <Col sm={2}>
                                                                {
                                                                    license.version
                                                                }
                                                            </Col>
                                                            <Col sm={3}>
                                                                {
                                                                    license.license
                                                                }
                                                            </Col>
                                                            <Col sm={3}>
                                                                {license.publisher ||
                                                                    "-"}
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                )
                                            )}
                                            <div ref={loadMoreRef}>
                                                {isLoadingMore && (
                                                    <div className="table-row">
                                                        <Row>
                                                            <Col className="text-center">
                                                                Loading more...
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </Col>
                            </Row>
                        </div>
                    </Col>
                </Row>
            </Container>

            <LegalDocumentsModal
                show={showLegalDocumentsModal}
                onHide={() => setShowLegalDocumentsModal(false)}
                documentType={legalDocumentType}
            />
        </div>
    );
};

export default About;
